import {
  Briefcase,
  Car,
  ChevronRight,
  ClipboardList,
  DollarSign,
  Info,
  Users,
} from 'lucide-react';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { MoBrainIcon } from '@/components/mobrain/MoBrainIcon';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useGetOneToOneNoteQuery } from '@/services/api/matterNotesService';
import {
  type RecommendedAction,
  type RiskTier,
  useGetLatestRiskSnapshotQuery,
} from '@/services/api/mobrainService';
import { IncidentNote, NOTE_TYPES } from '@/services/types/matter-notes-types';

import { DetailedMatter } from '../../services/types/client-intake-types';
import KeyConsiderations from '../key_considerations/KeyConsiderations';
import { INCIDENT_MATTER_NOTE_CONFIG } from '../matter-notes-v2/constants';
import { IncidentNoteForm } from '../matter-notes-v2/form/IncidentNoteForm';
import { MatterClientNoteV2 } from '../matter-notes-v2/matter-client-v2';
import { MatterServicesTab } from '../matters/MatterServicesTab';
import { useMiniTortClaim } from '../mini-tort/hooks/useMiniTortClaim';
import { MiniTortOverview } from '../mini-tort/MiniTortOverview';
import { CallsCard } from './CallsCard';
import { CaseAssessmentCard } from './CaseAssessmentCard';
import { CaseIngestionBanner } from './CaseIngestionBanner';
import { CaseOverviewCard } from './CaseOverviewCard';
import { DamagesCard } from './DamagesCard';
import { useHumanIncidentSummary } from './hooks/useHumanIncidentSummary';
import { InitialReviewBanner } from './InitialReviewBanner';
import { InsuranceCards } from './InsuranceCards';
import { LettersCard } from './LettersCard';
import { MedicalExpensesCard } from './MedicalExpensesCard';
import { PeopleTab } from './PeopleTab';
import { PoliceReportInfoCard } from './PoliceReportInfoCard';
import { ReferralSourceCard } from './ReferralSourceCard';
import { RelatedMatters } from './RelatedMatters';
import { StatusSummaryCard } from './StatusSummaryCard';

const RISK_TIER_STYLES: Record<RiskTier, string> = {
  LOW: 'border-green-200 bg-green-50 text-green-800',
  MEDIUM: 'border-yellow-200 bg-yellow-50 text-yellow-800',
  HIGH: 'border-orange-200 bg-orange-50 text-orange-800',
  CRITICAL: 'border-red-200 bg-red-50 text-red-800',
  UNKNOWN: 'border-gray-200 bg-gray-50 text-gray-700',
};

const ACTION_LABELS: Record<RecommendedAction, string> = {
  CONTACT_CLIENT: 'Contact Client',
  CONFIRM_COVERAGE_LIMITS: 'Confirm Coverage Limits',
  ESCALATE_CARE: 'Escalate Care',
  CONTINUE_CONSERVATIVE_CARE: 'Continue Conservative Care',
  CONTINUE_AGGRESSIVE_CARE: 'Continue Aggressive Care',
  MOVE_TO_DEMAND_REVIEW: 'Move to Demand Review',
  URGENT_ATTORNEY_ESCALATION: 'Urgent Attorney Escalation',
};

const RiskIndicatorBubble: React.FC<{ matterId: string }> = ({ matterId }) => {
  const navigate = useNavigate();
  const { data: snapshot } = useGetLatestRiskSnapshotQuery(matterId, {
    skip: !matterId,
  });

  if (!snapshot?.recommended_action) return null;

  const tierStyle =
    RISK_TIER_STYLES[snapshot.risk_tier ?? 'UNKNOWN'] ??
    RISK_TIER_STYLES.UNKNOWN;

  return (
    <button
      type="button"
      onClick={() => navigate(`/matters/${matterId}/risk`)}
      className={cn(
        'flex items-center gap-1.5 pl-2 pr-2.5 py-1 rounded-full border text-left transition-all hover:shadow-md hover:scale-105',
        tierStyle,
      )}
    >
      <MoBrainIcon className="h-4 w-4 flex-shrink-0" />
      <span className="text-xs font-semibold leading-tight truncate max-w-[180px]">
        {ACTION_LABELS[snapshot.recommended_action] ?? snapshot.recommended_action}
      </span>
      <ChevronRight className="h-3 w-3 flex-shrink-0 opacity-50" />
    </button>
  );
};

interface MatterProps {
  matter: DetailedMatter;
}

type TabId =
  | 'summary'
  | 'people'
  | 'accident'
  | 'insurance'
  | 'services'
  | 'considerations';

interface Tab {
  id: TabId;
  label: string;
  icon: React.ReactNode;
}

const Matter = ({ matter }: MatterProps) => {
  const [activeTab, setActiveTab] = useState<TabId>('summary');

  // Check if this is a mini-tort matter
  const {
    isMiniTortMatter,
    claim: miniTortClaim,
    isLoading: isMiniTortLoading,
  } = useMiniTortClaim(matter.id, matter.firm?.id);

  const hasKeyConsiderations =
    matter.key_considerations && matter.key_considerations.length > 0;

  // Fetch incident note directly
  const { data: incidentNote, refetch: refetchIncidentNote } =
    useGetOneToOneNoteQuery({
      matterId: matter.id,
      noteType: NOTE_TYPES.INCIDENT,
    });

  // Check for human-entered incident summary (ingestion note or intake description)
  const { humanSummary, source: humanSummarySource } =
    useHumanIncidentSummary({
      matterId: matter.id,
      intakeId: matter.intake_id,
    });

  // Define tabs - only show considerations if they have content
  const tabs: Tab[] = [
    { id: 'summary', label: 'Summary', icon: <Info className="h-3 w-3" /> },
    { id: 'people', label: 'People', icon: <Users className="h-3 w-3" /> },
    { id: 'accident', label: 'Accident', icon: <Car className="h-3 w-3" /> },
    {
      id: 'insurance',
      label: 'Insurance & Damages',
      icon: <DollarSign className="h-3 w-3" />,
    },
    {
      id: 'services',
      label: 'Scope of Work',
      icon: <ClipboardList className="h-3 w-3" />,
    },
    ...(hasKeyConsiderations
      ? [
          {
            id: 'considerations' as TabId,
            label: 'Key Considerations',
            icon: <Briefcase className="h-3 w-3" />,
          },
        ]
      : []),
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'summary':
        // Mini-tort matters get a simplified overview with the claim card
        if (isMiniTortMatter && miniTortClaim) {
          return (
            <MiniTortOverview matter={matter} miniTortClaim={miniTortClaim} />
          );
        }

        // Prevent flash of regular overview while mini-tort data is loading
        if (isMiniTortLoading) {
          return (
            <div className="space-y-3">
              <div className="rounded-lg border border-zinc-200 bg-white p-4">
                <Skeleton className="h-6 w-full mb-3" />
                <Skeleton className="h-4 w-40 mb-4" />
                <Skeleton className="h-8 w-24" />
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                <Skeleton className="h-48 rounded-lg" />
                <Skeleton className="h-48 rounded-lg" />
              </div>
            </div>
          );
        }

        return (
          <div className="space-y-4">
            <CaseIngestionBanner matterId={matter.id} />
            <InitialReviewBanner matterId={matter.id} />

            {/* Case Overview Card with Risk Bubble overlay */}
            <div className="relative">
              <div className="absolute -top-2 -right-1 z-10">
                <RiskIndicatorBubble matterId={matter.id} />
              </div>
              <CaseOverviewCard
                client={matter.clients?.[0]}
                incidentNote={incidentNote as IncidentNote | null}
                matter={matter}
                humanSummary={humanSummary}
                humanSummarySource={humanSummarySource}
              />
            </div>

            {/* Client Information */}
            {matter.clients?.[0] && (
              <MatterClientNoteV2
                matterId={matter.id}
                client={matter.clients[0]}
              />
            )}

            {/* Status Summary Card */}
            <StatusSummaryCard
              matterId={matter.id}
              matterStatus={matter.status}
            />

            {/* Law Firm Information */}
            {/* <LawFirmCard matter={matter} /> */}
            {/* Case Assessment Card - from intake */}
            <CaseAssessmentCard intakeId={matter.intake_id} />
            {/* Referral Source - editable */}
            <ReferralSourceCard
              matterId={matter.id}
              referralSource={matter.referral_source_description}
            />
          </div>
        );

      case 'people':
        return (
          <div className="space-y-4">
            <PeopleTab matterId={matter.id} />
          </div>
        );

      case 'accident':
        return (
          <div className="space-y-4">
            {/* Accident Details */}
            <IncidentNoteForm
              config={INCIDENT_MATTER_NOTE_CONFIG}
              matterId={matter.id}
              note={(incidentNote as IncidentNote) || null}
              onSaved={() => refetchIncidentNote()}
            />

            {/* Police Report Info */}
            <PoliceReportInfoCard matterId={matter.id} />

            {/* Calls - will auto-hide if no calls */}
            <CallsCard matterId={matter.id} />

            {/* Related Cases */}
            <RelatedMatters matterId={matter.id} />
          </div>
        );

      case 'insurance':
        return (
          <div className="space-y-4">
            {/* Insurance Cards - Client & Defendant side by side */}
            <InsuranceCards matterId={matter.id} />

            {/* Claim Information */}
            {/* <ClaimInformationCard matterId={matter.id} /> */}

            {/* Medical Expenses Summary */}
            <MedicalExpensesCard matterId={matter.id} />

            {/* Letters & Correspondence */}
            <LettersCard matter={matter} />

            {/* Damages */}
            <DamagesCard matterId={matter.id} />
          </div>
        );

      case 'services':
        return <MatterServicesTab matterId={matter.id} />;

      case 'considerations':
        return (
          <div className="space-y-4">
            <KeyConsiderations />
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="h-full flex flex-col">
      {/* Horizontal Tab Navigation - centered */}
      <div className="bg-white sticky top-0 z-10">
        <div className="max-w-4xl mx-auto border-b border-zinc-200">
          <nav
            className="flex justify-center gap-1"
            aria-label="Matter sections"
          >
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  'flex items-center gap-1 px-3 py-1.5 text-xs font-medium transition-colors border-b-2 -mb-px',
                  activeTab === tab.id
                    ? 'border-primary text-primary'
                    : 'border-transparent text-zinc-500 hover:text-zinc-700 hover:border-zinc-300',
                )}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-4xl py-2 mx-auto">{renderTabContent()}</div>
      </div>
    </div>
  );
};

export default Matter;
